(() => {
  if (window.__athenaHomeCartBridgeInit) return;
  window.__athenaHomeCartBridgeInit = true;

  if (!document.querySelector("[data-athena-home-hero]")) return;

  const hasCurrentCartShell =
    document.querySelector("[data-athena-cart-drawer]") &&
    document.querySelector("[data-athena-cart-toggle]");
  if (hasCurrentCartShell) return;

  const cartSectionId = "athena-cart-drawer";
  let drawerPromise = null;

  const getDrawer = () => document.querySelector("[data-athena-cart-drawer]");

  const updateHeaderCount = (count) => {
    document
      .querySelectorAll("[data-athena-header] a[href='/cart'], [data-athena-header] a[href$='/cart']")
      .forEach((node) => {
        const baseLabel =
          node.dataset.cartBaseLabel ||
          node.textContent.replace(/\s*\(\d+\)\s*$/, "").trim() ||
          "Cart";

        node.dataset.cartBaseLabel = baseLabel;
        node.textContent = count > 0 ? `${baseLabel} (${count})` : baseLabel;
      });
  };

  const setStatus = (message) => {
    const drawer = getDrawer();
    const status = drawer?.querySelector("[data-athena-cart-status]");
    if (!status) return;
    status.textContent = message || "";
  };

  const setBusy = (busy) => {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.dataset.cartBusy = busy ? "true" : "false";
  };

  const replaceDrawerMarkup = (html) => {
    const drawer = getDrawer();
    if (!drawer || !html) return;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const nextDrawer = doc.querySelector("[data-athena-cart-drawer]");
    if (!nextDrawer) return;

    drawer.setAttribute("data-cart-count", nextDrawer.getAttribute("data-cart-count") || "0");
    drawer.innerHTML = nextDrawer.innerHTML;
    updateHeaderCount(Number(drawer.getAttribute("data-cart-count") || "0"));
  };

  const buildSectionUrl = () => {
    const url = new URL(window.location.href);
    if (!url.pathname || url.pathname === "/cart") {
      url.pathname = "/";
    }
    url.searchParams.delete("cart_drawer");
    url.searchParams.set("section_id", cartSectionId);
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const mountDrawer = (html) => {
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const sectionWrapper = doc.querySelector("#shopify-section-athena-cart-drawer");
    const drawer = doc.querySelector("[data-athena-cart-drawer]");
    if (!drawer) return null;

    if (getDrawer()) return getDrawer();

    if (sectionWrapper) {
      document.body.appendChild(sectionWrapper);
    } else {
      document.body.appendChild(drawer);
    }

    return getDrawer();
  };

  const requestCart = async (endpoint, payload, useFormData = false) => {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: useFormData
        ? {
            Accept: "application/json",
          }
        : {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
      body: useFormData ? payload : JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || data.message || "Could not update cart.");
    }

    return data;
  };

  const bindDrawer = (drawer) => {
    if (!drawer || drawer.dataset.bridgeBound === "true") return;
    drawer.dataset.bridgeBound = "true";

    drawer.addEventListener("close", () => {
      document.body.classList.remove("athena-cart-open");
    });

    drawer.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeDrawer();
    });

    drawer.addEventListener("click", async (event) => {
      const activeDrawer = getDrawer();
      if (!activeDrawer) return;

      const closeTrigger = event.target.closest("[data-athena-cart-close], [data-athena-cart-continue]");
      if (closeTrigger) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.target === activeDrawer) {
        closeDrawer();
        return;
      }

      const removeTrigger = event.target.closest("[data-athena-cart-remove]");
      if (removeTrigger && activeDrawer.dataset.cartBusy !== "true") {
        event.preventDefault();
        setBusy(true);
        setStatus("");

        try {
          const payload = await requestCart("/cart/change.js", {
            line: Number(removeTrigger.dataset.line),
            quantity: 0,
            sections: cartSectionId,
            sections_url: `${window.location.pathname}${window.location.search}`,
          });
          replaceDrawerMarkup(payload.sections?.[cartSectionId] || "");
          setStatus("Cart updated.");
          await openDrawer();
        } catch (error) {
          setStatus(error.message || "Could not update cart.");
        } finally {
          setBusy(false);
        }

        return;
      }

      const quantityTrigger = event.target.closest("[data-athena-cart-quantity]");
      if (quantityTrigger && activeDrawer.dataset.cartBusy !== "true") {
        event.preventDefault();

        const currentQuantity = Number(quantityTrigger.dataset.quantity || "0");
        const delta = Number(quantityTrigger.dataset.delta || "0");
        const nextQuantity = currentQuantity + delta;
        if (nextQuantity < 1) return;

        setBusy(true);
        setStatus("");

        try {
          const payload = await requestCart("/cart/change.js", {
            line: Number(quantityTrigger.dataset.line),
            quantity: nextQuantity,
            sections: cartSectionId,
            sections_url: `${window.location.pathname}${window.location.search}`,
          });
          replaceDrawerMarkup(payload.sections?.[cartSectionId] || "");
          setStatus("Cart updated.");
          await openDrawer();
        } catch (error) {
          setStatus(error.message || "Could not update cart.");
        } finally {
          setBusy(false);
        }
      }
    });
  };

  const ensureDrawer = async () => {
    const existing = getDrawer();
    if (existing) {
      bindDrawer(existing);
      return existing;
    }

    if (!drawerPromise) {
      drawerPromise = fetch(buildSectionUrl(), {
        credentials: "same-origin",
        headers: {
          Accept: "text/html",
        },
      })
        .then((response) => response.text())
        .then((html) => {
          const drawer = mountDrawer(html);
          if (!drawer) throw new Error("Could not load cart drawer.");
          bindDrawer(drawer);
          return drawer;
        })
        .finally(() => {
          drawerPromise = null;
        });
    }

    return drawerPromise;
  };

  const openDrawer = async () => {
    const drawer = await ensureDrawer();
    if (!drawer) return;

    if (!drawer.open) {
      document.querySelectorAll("dialog[open]").forEach((dialog) => {
        if (dialog === drawer) return;
        if (typeof dialog.close === "function") {
          dialog.close();
        } else {
          dialog.removeAttribute("open");
        }
      });

      if (typeof drawer.showModal === "function") {
        drawer.showModal();
      } else {
        drawer.setAttribute("open", "open");
      }
    }

    document.body.classList.add("athena-cart-open");
  };

  const closeDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return;

    if (drawer.open && typeof drawer.close === "function") {
      drawer.close();
    } else {
      drawer.removeAttribute("open");
    }

    document.body.classList.remove("athena-cart-open");
  };

  document.addEventListener("click", async (event) => {
    const cartToggle = event.target.closest(
      "[data-athena-header] a[href='/cart'], [data-athena-header] a[href$='/cart']"
    );
    if (!cartToggle) return;

    event.preventDefault();
    setStatus("");
    await openDrawer();
  });

  document.addEventListener(
    "submit",
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const isHomeAddForm =
        form.matches("[data-role='family-form']") || form.matches("[data-role='spotlight-form']");
      if (!isHomeAddForm) return;

      if (event.submitter?.closest(".shopify-payment-button")) return;

      const activeDrawer = await ensureDrawer();
      if (activeDrawer.dataset.cartBusy === "true") {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      setBusy(true);
      setStatus("");

      try {
        const formData = new FormData(form);
        formData.set("sections", cartSectionId);
        formData.set("sections_url", `${window.location.pathname}${window.location.search}`);

        const payload = await requestCart("/cart/add.js", formData, true);
        replaceDrawerMarkup(payload.sections?.[cartSectionId] || "");
        setStatus("Added to cart.");
        await openDrawer();
      } catch (error) {
        setStatus(error.message || "Could not add this item.");
        await openDrawer();
      } finally {
        setBusy(false);
      }
    },
    true
  );

  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get("cart_drawer") === "open") {
    ensureDrawer()
      .then(() => openDrawer())
      .finally(() => {
        currentUrl.searchParams.delete("cart_drawer");
        const nextUrl =
          currentUrl.pathname +
          (currentUrl.searchParams.toString() ? `?${currentUrl.searchParams.toString()}` : "") +
          currentUrl.hash;
        window.history.replaceState({}, "", nextUrl || "/");
      });
  }
})();
